/**
 * Decorations that give the WYSIWYG view its LyX look: section numbers, list bullets and
 * numbers, float/caption numbers, footnote numbers, equation numbers, comment headers,
 * and paragraph depth/alignment classes.
 */
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import type { Node as PMNode } from 'prosemirror-model';
import { ENUM_STYLES, ITEMIZE_BULLETS, isNumberedSection, sectionLevel } from '../layouts';
import { parseHeader } from '@overlyx/core';
import { parseFormula } from '@overlyx/core';
import { editorContext } from '../context';
import { authorInitials, authorHue } from '../commentops';

import type { EditorView } from 'prosemirror-view';
export const numberingKey = new PluginKey<DecorationSet>('lyx-numbering');

interface Counters { sec: number[]; enumr: number[]; enumOpen: boolean[]; figure: number; table: number; foot: number; eq: number; theorem: number }

function build(doc: PMNode): DecorationSet {
  const decos: Decoration[] = [];
  const c: Counters = { sec: [0, 0, 0, 0, 0, 0, 0], enumr: [0, 0, 0, 0, 0, 0, 0, 0, 0], enumOpen: [false, false, false, false, false, false, false, false, false], figure: 0, table: 0, foot: 0, eq: 0, theorem: 0 };
  let appendix = false;
  let prevLayout = '', prevDepth = 0;
  const notes = new Map<string, { node: PMNode; n: number }>();
  const secLabel = (level: number) => {
    const parts: string[] = [];
    for (let i = 0; i <= level + 1; i++) parts.push(String(c.sec[i]));
    // drop leading chapter/part zeros for article-like classes
    while (parts.length > 1 && parts[0] === '0') parts.shift();
    if (appendix && parts.length) parts[0] = String.fromCharCode(64 + Number(parts[0]));
    return parts.join('.');
  };

  const walkInsets = (node: PMNode, pos: number, floatType: string | null) => {
    node.forEach((child, off) => {
      const p = pos + off;
      if (child.type.name === 'inset') {
        const name = child.attrs.name as string, arg = child.attrs.arg as string;
        if (name === 'Foot') {
          // a markdown note referred to again (`[^a]` … `[^a]`: the same label and text) keeps its number
          const label = /^label "(.*)"$/.exec((JSON.parse(String(child.attrs.params || '[]')) as string[]).find(l => l.startsWith('label ')) ?? '')?.[1];
          const first = label !== undefined ? notes.get(label) : undefined;
          const again = !!first && first.node.content.eq(child.content);
          const n = again ? first!.n : ++c.foot;
          if (label !== undefined && !first) notes.set(label, { node: child, n });
          // (--fn-number: the number for the label's and the reference's CSS, which attr() cannot reach from inside)
          decos.push(Decoration.node(p, p + child.nodeSize, { 'data-number': String(n), style: `--fn-number:"${n}"`, ...(again ? { 'data-repeat': '' } : {}) }));
        } else if (name === 'Caption') {
          const ft = floatType ?? 'figure';
          let label = '';
          if (ft === 'figure') { c.figure++; label = `Figure ${c.figure}:`; }
          else if (ft === 'table') { c.table++; label = `Table ${c.table}:`; }
          else label = `${ft[0].toUpperCase()}${ft.slice(1)}:`;
          decos.push(Decoration.node(p, p + child.nodeSize, { 'data-caption-label': label }));
        } else if (name === 'Note' && arg === 'Comment') {
          // comment thread headers: "Name (2026-08-26 14:03):" — shown as an avatar + name + time line
          // (the avatar and the muted time come from the attributes; the parentheses and the colon are
          // made invisible, the text itself stays where it is)
          child.forEach((para, poff) => {
            const text = para.textContent;
            const h = parseHeader(text.trim());
            if (!h) return;
            const start = p + 1 + poff;
            decos.push(Decoration.node(start, start + para.nodeSize, { class: 'comment-header' + (h.resolved ? ' resolved' : ''), 'data-author': h.author, 'data-time': h.time, 'data-initials': authorInitials(h.author), style: `--who-hue:${authorHue(h.author)}` }));
            const t0 = start + 1;   // first character of the paragraph
            const iAuthor = text.indexOf(h.author);
            if (iAuthor >= 0) decos.push(Decoration.inline(t0 + iAuthor, t0 + iAuthor + h.author.length, { class: 'comment-who' }));
            const iTime = text.indexOf('(' + h.time + ')', iAuthor >= 0 ? iAuthor + h.author.length : 0);
            if (iTime >= 0) {
              decos.push(Decoration.inline(t0 + iTime, t0 + iTime + 1, { class: 'comment-hide' }));
              decos.push(Decoration.inline(t0 + iTime + 1, t0 + iTime + 1 + h.time.length, { class: 'comment-when' }));
              decos.push(Decoration.inline(t0 + iTime + 1 + h.time.length, t0 + text.length, { class: h.resolved ? 'comment-hide comment-resolved-tag' : 'comment-hide' }));
            }
          });
        }
        const inner = name === 'Float' || name === 'Wrap' ? arg : floatType;
        // recurse into paragraphs of the inset
        child.forEach((para, poff) => {
          if (para.type.name === 'paragraph') {
            decorateParagraph(para, p + 1 + poff, true);
            walkInsets(para, p + 1 + poff + 1, inner);
          }
        });
      } else if (child.type.name === 'table') {
        child.forEach((row, roff) => row.forEach((cell, coff) => cell.forEach((para, paoff) => {
          const pp = p + 1 + roff + 1 + coff + 1 + paoff;
          if (para.type.name === 'paragraph') { decorateParagraph(para, pp, true); walkInsets(para, pp + 1, floatType); }
        })));
      } else if (child.type.name === 'math_display') {
        const h = parseFormula(String(child.attrs.latex));
        const n = h.type === 'simple' || h.type === 'none' || h.type === 'unknown' ? 0 : h.type === 'equation' ? (h.numberedRows[0] === true ? 1 : 0) : h.type === 'multline' ? (h.numberedRows.some(x => x === true) ? 1 : 0) : h.numberedRows.filter(x => x === true).length;
        if (n > 0) {
          const nums: string[] = [];
          for (let i = 0; i < n; i++) nums.push(String(++c.eq));
          decos.push(Decoration.node(p, p + child.nodeSize, { 'data-eqnum': nums.map(x => `(${x})`).join('\n') }));
        }
      }
    });
  };

  const decorateParagraph = (para: PMNode, pos: number, inInset: boolean) => {
    const layout = para.attrs.layout as string;
    const depth = para.attrs.depth as number;
    const attrs: Record<string, string> = {};
    let cls = '';
    if (!inInset) {
      if (para.attrs.appendix && !appendix) { appendix = true; c.sec.fill(0); }   // \appendix restarts the top-level counter, lettered from here on
      const lvl = sectionLevel(layout);
      if (lvl !== null && isNumberedSection(layout)) {
        const idx = lvl + 1;
        c.sec[idx]++;
        for (let i = idx + 1; i < c.sec.length; i++) c.sec[i] = 0;
        if (lvl <= (liveDepth ?? editorContext.meta?.secnumdepth ?? 3)) attrs['data-label'] = secLabel(lvl);
      }
    }
    // An enumerate environment at depth d stays open across deeper paragraphs (a nested list or a
    // continuation paragraph of the item) and is closed by any other paragraph at depth <= d (LaTeX):
    // its counter restarts only when a new environment opens at that depth.
    for (let d = depth + 1; d < c.enumOpen.length; d++) c.enumOpen[d] = false;
    if (layout !== 'Enumerate') c.enumOpen[depth] = false;
    if (layout === 'Enumerate') {
      if (!c.enumOpen[depth]) { c.enumr[depth] = 0; c.enumOpen[depth] = true; }
      c.enumr[depth] = (c.enumr[depth] ?? 0) + 1;
      attrs['data-label'] = ENUM_STYLES[depth % 4](c.enumr[depth]);
    } else if (layout === 'Itemize') {
      attrs['data-label'] = ITEMIZE_BULLETS[depth % 4];
    } else if (layout === 'Description' || layout === 'Labeling') {
      cls += ' lyx-description';
    } else if (/^(Theorem|Lemma|Corollary|Proposition|Conjecture|Definition|Example|Problem|Exercise|Remark|Claim|Fact|Case|Proof|Axiom|Notation|Solution|Question)\*?$/.test(layout)) {
      // consecutive paragraphs of the same layout form one environment (LyX): one label, one number
      const continues = prevLayout === layout && prevDepth === depth;
      if (!layout.endsWith('*') && layout !== 'Proof') { if (!continues) c.theorem++; if (!continues) attrs['data-label'] = `${layout} ${c.theorem}.`; }
      else if (!continues) attrs['data-label'] = layout.replace(/\*$/, '') + '.';
      cls += ' lyx-theorem' + (continues ? ' lyx-theorem-cont' : '');
    }
    if (para.attrs.align) attrs['data-align'] = para.attrs.align;
    if (Object.keys(attrs).length || cls) decos.push(Decoration.node(pos, pos + para.nodeSize, { ...attrs, ...(cls ? { class: cls.trim() } : {}) }));
    prevLayout = layout; prevDepth = depth;
  };

  // layout documents: the paragraphs of every text box (and the notes); lists restart per box
  const walkLayout = (node: PMNode, pos: number) => {
    node.forEach((child, off) => {
      const p = pos + 1 + off;
      if (child.type.name === 'ol_group') { walkLayout(child, p); return; }
      if (child.type.name !== 'ol_box' && child.type.name !== 'ol_notes') return;
      c.enumOpen.fill(false);
      prevLayout = '';
      child.forEach((para, poff) => {
        if (para.type.name !== 'paragraph') return;
        decorateParagraph(para, p + 1 + poff, true);
        walkInsets(para, p + 1 + poff + 1, null);
      });
    });
  };

  doc.forEach((para, off) => {
    if (para.type.name === 'ol_page') { walkLayout(para, off); return; }
    if (para.type.name !== 'paragraph') return;
    decorateParagraph(para, off, false);
    walkInsets(para, off + 1, null);
  });
  return DecorationSet.create(doc, decos);
}


/**
 * The open document's \secnumdepth, from its settings as the shells report them (setDocumentNumbering,
 * next to setDocumentFonts) — the metadata arrives later, and a markdown document (unnumbered) showed
 * LaTeX's default numbers until its first edit; Document ▸ Settings changes it while it is open.
 */
let liveDepth: number | null = null;
const views = new Set<EditorView>();
export function setDocumentNumbering(headerLines: readonly string[]): void {
  const line = headerLines.find(l => l.startsWith('\\secnumdepth '));
  const d = line ? Number(line.slice(13)) : NaN;
  const next = Number.isFinite(d) ? d : null;
  if (next === liveDepth) return;
  liveDepth = next;
  for (const view of views) if (!view.isDestroyed) view.dispatch(view.state.tr.setMeta(numberingKey, true).setMeta('addToHistory', false));
}

export function numberingPlugin(): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: numberingKey,
    state: {
      init: (_c, state) => build(state.doc),
      apply: (tr, old, _o, newState) => (tr.docChanged || tr.getMeta(numberingKey) ? build(newState.doc) : old),
    },
    props: { decorations: (state) => numberingKey.getState(state) ?? null },
    view: view => { views.add(view); return { destroy: () => { views.delete(view); } }; },
  });
}
